import { AfterViewInit, Component, ContentChildren, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, QueryList, Renderer2, ViewChild } from '@angular/core';
import { BaseViewComponent, DrawerManager, DrawerState, DrawerWidth, Orientation } from '@sb/library/base';
import { IframeComponent } from '@sb/library/iframe';

@Component({
    selector: 'lib-drawer-layout',
    templateUrl: './drawer-layout.component.html',
    styleUrls: ['./drawer-layout.component.scss'],
})

export class DrawerLayoutComponent extends BaseViewComponent implements AfterViewInit, OnDestroy {
    private _width: DrawerWidth = DrawerWidth.Medium;
    @Input()
    set width(value: DrawerWidth) {
        this._width = value;
    }
    
    @Input() hasFullScreenMode: boolean = true;
    @Input() hasDeleteIcon: boolean = false;
    @Input() set includeFooter(value: boolean) {
        this._includeFooter = value;
        if (this._includeFooter && this._bgColor) {
            this.DetectChanges();
            const drawerFooter = this.drawerFooter?.nativeElement;
            if (drawerFooter) {
                drawerFooter.style.backgroundColor = this._bgColor;
            }
        }
    };
    @Input() includeHeader: boolean = true; 
    @Input() disableBackButton: boolean = false;
    
    @Input() qaTag: string = '';

    @Output() deleteClicked: EventEmitter<void> = new EventEmitter();
    windowWidth!: number;
    private _includeFooter: boolean = true;
    private _bgColor: string = '';
    private _isOpenedFromAnotherDrawer: boolean = false;
    private _drawerHeaderObserver: MutationObserver | null = null;
    private _drawerContentObserver: MutationObserver | null = null;


    @ViewChild('layoutContainer', {static: false}) layoutContainerElement!: ElementRef<HTMLElement>;
    @ContentChildren(IframeComponent, {descendants: true}) iframes?: QueryList<IframeComponent>;
    @ViewChild('drawerHeader', { static: true }) drawerHeader!: ElementRef;
    @ViewChild('drawerFooter', { static: false }) drawerFooter!: ElementRef;
    @ViewChild('additionalStickyHeader', { static: false }) additionalStickyHeader!: ElementRef;
    @ViewChild('drawerContent', { static: false }) drawerContent!: ElementRef;
    @ViewChild('transientHeader', { static: false }) transientHeader!: ElementRef;


    constructor(
        private _Renderer: Renderer2,
        private _DrawerManager: DrawerManager
    ) {
        super();
        this._isOpenedFromAnotherDrawer = this._DrawerManager.numberOfDrawers > 1;
     
    }

    override ngOnInit() {
        super.ngOnInit();
        this.windowWidth = window.innerWidth;
    }

    ngAfterViewInit() {
        const drawerHeader = this.drawerHeader.nativeElement as HTMLElement;
        const drawerFooter = this.drawerFooter?.nativeElement;
        const drawerContent = this.drawerContent.nativeElement as HTMLElement;
        const layoutContainer = this.layoutContainerElement.nativeElement as HTMLElement;
        const additionalStickyHeader = this.additionalStickyHeader?.nativeElement as HTMLElement;
        const transientHeader = this.transientHeader?.nativeElement as HTMLElement;

        this._bgColor = this.GetRealBackgroundColor(drawerHeader);
        drawerHeader.style.backgroundColor = this._bgColor;
        if (drawerFooter) {
            drawerFooter.style.backgroundColor = this._bgColor;
        }
        this._Renderer.setStyle(layoutContainer, "width", ((this.windowWidth < this._width) || this.isMobile) ? "100vw" : this._width + "px");

        setTimeout(() => {
            this.UpdateStickyHeaders();
        });

        setTimeout(() => { //has to wait until animation is done
            drawerHeader.focus();
        }, 350);

    
        this._drawerHeaderObserver = new MutationObserver(() => {
            this.UpdateStickyHeaders();
        });

        this._drawerHeaderObserver.observe(drawerHeader, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['style', 'class']
        });

        layoutContainer.style.height = Math.max(drawerContent.scrollHeight, window.innerHeight) + 'px';

        this._drawerContentObserver = new MutationObserver(() => {
            const drawerHeaderHeight = drawerHeader?.clientHeight ?? 0;
            const additionalStickyHeaderHeight = additionalStickyHeader?.clientHeight ?? 0;
            const transientHeaderHeight = transientHeader?.clientHeight ?? 0;

            const headerHeights = drawerHeaderHeight + additionalStickyHeaderHeight + transientHeaderHeight;
            const totalContentHeight = drawerContent.scrollHeight + headerHeights + 1;

            layoutContainer.style.height = Math.max(totalContentHeight, window.innerHeight) + 'px';
        });


        this._drawerContentObserver.observe(this.drawerContent.nativeElement, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['style', 'class']
        });

        if (this._DrawerManager.previousDrawerLayoutState !== null) {
            this._width = this._DrawerManager.previousDrawerLayoutState.Size;
            const isFullscreen = this._DrawerManager.previousDrawerLayoutState.State === DrawerState.Fullscreen;
            this._DrawerManager.currentDrawer.State = isFullscreen ? DrawerState.Fullscreen : DrawerState.Default;
            this._Renderer.setStyle(layoutContainer, "width", ((this.windowWidth < this._width) || this.isMobile || isFullscreen) ? "100vw" : this._width + "px");
        } 
        this._DrawerManager.currentDrawer.Size = this._width;
        this._DrawerManager.currentDrawer.LayoutElement = layoutContainer
        

    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        if (this._drawerHeaderObserver) {
            this._drawerHeaderObserver.disconnect();
        }
    }


    private UpdateStickyHeaders() {
        const drawerHeader = this.drawerHeader.nativeElement as HTMLElement;
        const topElement = document.getElementById('tab-group-top-element');
        if (topElement) {
            topElement.style.top = getComputedStyle(drawerHeader).height;
            topElement.style.zIndex = '9999';
        }
        if (this.additionalStickyHeader) {
            this.additionalStickyHeader.nativeElement.style.backgroundColor = this._bgColor;
            this.additionalStickyHeader.nativeElement.style.top = getComputedStyle(drawerHeader).height;
            this.additionalStickyHeader.nativeElement.style.zIndex = '9999';
        }
    }

    ToggleFullscreen(): void {
        switch (this._DrawerManager.currentDrawer.State) {
            case DrawerState.Closed:
            case DrawerState.Default:
                this._Renderer.setStyle(this.layoutContainerElement.nativeElement, "width", "100vw");
                this._DrawerManager.currentDrawer.State = DrawerState.Fullscreen;
                this._DrawerManager.currentDrawer.Overlay.updateSize({width: '100%'});
                break;
            case DrawerState.Fullscreen:
                for (let i = 0; i < this._DrawerManager.numberOfDrawers; i++) {
                    const drawer =  this._DrawerManager.drawers[this._DrawerManager.drawers.length - (i + 1)];
                    this._Renderer.setStyle(drawer.LayoutElement, "width", this._width + "px");
                    drawer.State = DrawerState.Default;
                    drawer.Overlay.updateSize({width: 'fit-content'});
                }
                break;
        }
            

        this.iframes?.forEach((iframe: IframeComponent) => {
            iframe.Resize();
        })
    }

    DeleteClicked(): void {
        this.deleteClicked.emit();
    }

    Back(): void {
        this.CloseOpenFormFields();
        this.UpdatePreviousDrawerLayouts();
        this._DrawerManager.CloseDrawer();
    }

    Close(): void {
        this.UpdatePreviousDrawerLayouts();
        if (this._isOpenedFromAnotherDrawer) {
            this._DrawerManager.CloseAllDrawers();
        } else {
            this._DrawerManager.CloseDrawer();
        }
    }

    private UpdatePreviousDrawerLayouts() {
        if (this._DrawerManager.currentDrawer.State === DrawerState.Fullscreen) {
            for (let i = 0; i < this._DrawerManager.numberOfDrawers; i++) {
                const drawer =  this._DrawerManager.drawers[this._DrawerManager.drawers.length - (i + 1)];
                this._Renderer.setStyle(drawer.LayoutElement, "width", '100vw');
                drawer.State = DrawerState.Fullscreen;
                drawer.Overlay.updateSize({width: '100%'});
            }
        }
    }

    private GetRealBackgroundColor(elem: HTMLElement | null): string { // this is from the Style Manager - but needs to be duplicated here
        const transparent = 'rgba(0, 0, 0, 0)';
        if (!elem) {
            return transparent;
        }
        var backgroundColor = getComputedStyle(elem).backgroundColor;

        if (backgroundColor === transparent) {
            return this.GetRealBackgroundColor(elem.parentElement);
        } else {
            return backgroundColor;
        }
    }

    get orientation(): Orientation {
        return this.isMobile ? Orientation.Vertical : Orientation.Horizontal;
    }

    get drawerState(): DrawerState {
        return this._DrawerManager.currentDrawer.State;
    }

    get DrawerState(): typeof DrawerState {
        return DrawerState;
    }

    get DrawerWidth(): typeof DrawerWidth {
        return DrawerWidth;
    }
    get includeFooter(): boolean {
        return this._includeFooter;
    }

    get isOpenedFromAnotherDrawer(): boolean {
        return this._isOpenedFromAnotherDrawer;
    }

    get width(): DrawerWidth {
        return this._width;
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.windowWidth = window.innerWidth;
    }
}
